import { useCallback, useMemo, useRef, useState } from 'react';
import {
  createActionsForParent,
  type CreateActionDefinition,
} from '../../../../constants/createActions';
import { ANALYTICS_EVENTS } from '../../../../constants/analytics';
import { useCreateAndNavigate } from '../../../../hooks/entity/useCreateAndNavigate';
import { useSpreadsheetsEnabled } from '../../../../hooks/data/useSpreadsheetsEnabled';
import { safeAnalytics } from '../../../../utils/analytics';
import { CREATE_BODIES } from '../bodies/createBodies';
import { destinationParent, destinationParentType } from '../destination/createDestination';
import type { CreateModalController } from './useCreateModalController';

/**
 * Step 1's choices for the current destination and what choosing one does:
 * a type with a body opens its step 2; any other commits at once (creates
 * the note and opens it), exactly as the menus did.
 */
export function useCreateChoices(ctrl: CreateModalController, onDone: () => void) {
  const spreadsheetsEnabled = useSpreadsheetsEnabled();
  const surface = ctrl.request?.surface ?? 'create_modal';
  const commit = useCreateAndNavigate(surface, ctrl.reportDestinationProblem);
  const { destination, source, setView } = ctrl;
  // A commit awaits the create while the modal stays open: a second click or
  // letter must not make a second note.
  const committing = useRef(false);
  const [isCommitting, setIsCommitting] = useState(false);

  const actions = useMemo(
    () =>
      destination
        ? createActionsForParent(destinationParentType(destination)).filter(
            (a) => a.action.kind !== 'spreadsheet' || spreadsheetsEnabled,
          )
        : [],
    [destination, spreadsheetsEnabled],
  );

  /** Whether a type opens a step 2 (rather than committing from step 1). */
  const hasBody = useCallback((id: string) => !!CREATE_BODIES[id], []);

  const choose = useCallback(
    async (definition: CreateActionDefinition) => {
      if (!destination || committing.current) return;
      safeAnalytics.track(ANALYTICS_EVENTS.CREATE_MENU_ITEM_CLICKED, {
        create_type: definition.id,
        surface,
        parent_entity_type: destinationParentType(destination),
        step: 'choose',
        destination_source: source,
      });
      if (hasBody(definition.id)) {
        setView({ kind: 'details', type: definition.id });
        return;
      }
      if (definition.action.kind !== 'artifact' || committing.current) return;
      committing.current = true;
      setIsCommitting(true);
      try {
        const scope = destinationParent(destination);
        const ok = await commit(
          scope,
          definition.action.artifactType,
          definition.action.captureMethod,
        );
        if (ok) onDone();
      } finally {
        committing.current = false;
        setIsCommitting(false);
      }
    },
    [destination, surface, source, setView, hasBody, commit, onDone],
  );

  return { actions, choose, isCommitting };
}
